/**
 * The four priority reports (FR-RPT-03/04/05/07).
 *
 * Pure. A report is a **table model**, not markup: columns with declared types, rows of
 * typed cells, and an optional totals row. The screen renders it, and Excel, CSV and PDF
 * each serialise the same object — which is FR-RPT-02 ("one export path serves every
 * report; adding a report does not mean writing new export code") expressed as a type
 * rather than as a promise.
 *
 * Cell types matter more than they look. FR-RPT-11 requires Excel exports to carry real
 * dates and real numbers, not strings, so the exporter has to be told which is which —
 * and the only place that knows is the column definition here.
 *
 * Money is carried as **paise**, unformatted, all the way to the serialiser. Formatting
 * at the point of display is what keeps an Excel cell a number that sums.
 */

import type { MonthKey } from './dates';
import { formatMonthKey, monthKeyOf, monthRange } from './dates';
import type { Filters } from './filters';
import type { Paise } from './money';
import { periodLabel, periodMonths, periodRange, type PeriodOptions } from './period';
import { UNCATEGORISED, sumRollups, type Rollup } from './rollups';
import { classifyOutflow, type Transaction } from './transactions';
import type { AccountType } from './accounts';

export type ReportId = 'detailed' | 'category' | 'income-expense' | 'monthly';

export interface ReportDefinition {
  id: ReportId;
  title: string;
  description: string;
}

/** V1's priority set. Account-wise (FR-RPT-06) and budget-vs-actual (FR-RPT-08) follow. */
export const REPORTS: ReportDefinition[] = [
  {
    id: 'detailed',
    title: 'Detailed transactions',
    description: 'Every matching row, as entered.',
  },
  {
    id: 'category',
    title: 'Category-wise',
    description: 'Spending grouped by category, largest first.',
  },
  {
    id: 'income-expense',
    title: 'Income vs expense',
    description: 'Month by month, with the net.',
  },
  {
    id: 'monthly',
    title: 'Monthly summary',
    description: 'Expenses, savings and outflow per month.',
  },
];

export type CellType = 'text' | 'date' | 'money' | 'number' | 'percent';

export interface ReportColumn {
  key: string;
  label: string;
  type: CellType;
  /** Carried only by the business PDF template (FR-RPT-13). */
  businessOnly?: boolean;
}

export type Cell = string | number | null;

export interface ReportRow {
  cells: Record<string, Cell>;
  /** Rendered heavier — a subtotal or a net line. */
  emphasis?: boolean;
}

export interface Report {
  id: ReportId;
  title: string;
  /** "FY 2026-27" or "01 Jan 2026 – 31 Jul 2026". Goes into the PDF header and filename. */
  periodLabel: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: ReportRow | null;
  /**
   * Where the figures came from (FR-RPT-16). Surfaced rather than internal: a report
   * built from rollups is a report built from a cache, and the user should be able to
   * tell when a number came from one.
   */
  source: 'transactions' | 'rollups';
  /** Human-readable, for the export footer and the on-screen chip row. */
  appliedFilters: string[];
}

export type AccountTypeLookup = (accountId: string) => AccountType | undefined;

export interface ReportContext {
  filters: Filters;
  accountName: (id: string) => string;
  categoryName: (id: string) => string;
  accountType: AccountTypeLookup;
  appliedFilters: string[];
  options?: PeriodOptions;
}

// ---------------------------------------------------------------------------
// FR-RPT-16 — when a rollup may stand in for the transactions
// ---------------------------------------------------------------------------

/**
 * May this report be built from stored rollups instead of transactions?
 *
 * Only when **nothing narrows the set beyond what a rollup stores**, and the period
 * covers whole months. Rollups hold totals per month, per category, per account and per
 * business flag; they hold nothing about tags, subcategories, currency, amount ranges,
 * free text or the follow-up flag. Answering a tag-filtered question from a rollup would
 * silently return the unfiltered total, which is worse than being slow.
 *
 * The whole-month condition is the one that is easy to miss. A custom range of
 * 1–15 August is served by August's rollup, which covers the whole month — so a range
 * that does not start on the 1st and end on the last day must fall back.
 */
export function canUseRollups(filters: Filters, options: PeriodOptions = {}): boolean {
  const narrowing: (keyof Filters)[] = [
    'accountId', 'type', 'categoryId', 'subcategoryId', 'tagId',
    'isBusiness', 'currency', 'minPaise', 'maxPaise', 'search', 'needsEnrichment',
  ];
  if (narrowing.some((key) => filters[key] !== undefined)) return false;

  const { start, end } = periodRange(filters.period, options);
  const months = periodMonths(filters.period, options);
  if (months.length === 0) return false;

  const firstOfMonth = start.endsWith('-01');
  const lastOfMonth = end === monthRange(end.slice(0, 7)).end;
  return firstOfMonth && lastOfMonth;
}

// ---------------------------------------------------------------------------
// FR-RPT-03 — detailed transactions
// ---------------------------------------------------------------------------

const DETAILED_COLUMNS: ReportColumn[] = [
  { key: 'date', label: 'Date', type: 'date' },
  { key: 'category', label: 'Category', type: 'text' },
  { key: 'description', label: 'Description', type: 'text' },
  { key: 'account', label: 'Account', type: 'text' },
  // The business template adds these two (FR-RPT-13); the personal one drops them.
  { key: 'payee', label: 'Payee', type: 'text', businessOnly: true },
  { key: 'reference', label: 'Reference', type: 'text', businessOnly: true },
  { key: 'amount', label: 'Amount', type: 'money' },
];

export function detailedReport(transactions: Transaction[], ctx: ReportContext): Report {
  const rows = [...transactions]
    .sort((a, b) => a.dateKey.localeCompare(b.dateKey) || a.id.localeCompare(b.id))
    .map((txn) => ({
      cells: {
        date: txn.dateKey,
        category: describeCategory(txn, ctx),
        description: txn.description ?? '',
        account:
          txn.type === 'transfer'
            ? `${ctx.accountName(txn.accountId)} → ${ctx.accountName(txn.toAccountId ?? '')}`
            : ctx.accountName(txn.accountId),
        payee: txn.payee ?? '',
        reference: txn.referenceNo ?? '',
        amount: signedPaise(txn),
      } satisfies Record<string, Cell>,
    }));

  return {
    id: 'detailed',
    title: 'Detailed transactions',
    periodLabel: periodLabel(ctx.filters.period),
    columns: DETAILED_COLUMNS,
    rows,
    totals: {
      cells: {
        date: null,
        category: '',
        description: `${rows.length} transaction${rows.length === 1 ? '' : 's'} · net movement`,
        account: '',
        payee: '',
        reference: '',
        amount: rows.reduce((sum, r) => sum + Number(r.cells.amount ?? 0), 0),
      },
      emphasis: true,
    },
    source: 'transactions',
    appliedFilters: ctx.appliedFilters,
  };
}

/**
 * The amount as it affects the account named on the row: income in, everything else out.
 *
 * Signed rather than absolute so the column **sums in Excel to the number shown in the
 * totals row** (FR-RPT-11). A transfer is negative here even though FR-TXN-05 keeps it out
 * of expenses, because the row names its source account — "IDFC → SBM CC" — and from that
 * account the money did leave. The total is therefore net movement across the listed rows,
 * not a spending figure; the spending figures are the monthly and category reports, which
 * apply the FR-DASH-02 classification properly.
 *
 * Zero was the alternative and is worse: a card payment showing 0.00 reads as a bug.
 */
function signedPaise(txn: Transaction): Paise {
  return txn.type === 'income' ? txn.amountInrPaise : -txn.amountInrPaise;
}

function describeCategory(txn: Transaction, ctx: ReportContext): string {
  if (txn.type === 'transfer') return 'Transfer';
  if (!txn.categoryId) return 'Uncategorised';
  const parent = ctx.categoryName(txn.categoryId);
  return txn.subcategoryId ? `${parent} → ${ctx.categoryName(txn.subcategoryId)}` : parent;
}

// ---------------------------------------------------------------------------
// FR-RPT-04 — category-wise
// ---------------------------------------------------------------------------

const CATEGORY_COLUMNS: ReportColumn[] = [
  { key: 'category', label: 'Category', type: 'text' },
  { key: 'count', label: 'Entries', type: 'number' },
  { key: 'amount', label: 'Amount', type: 'money' },
  { key: 'share', label: 'Share', type: 'percent' },
];

export function categoryReport(
  transactions: Transaction[],
  ctx: ReportContext,
  rollups?: Rollup[],
): Report {
  const totals = new Map<string, { paise: Paise; count: number }>();
  const useRollups = rollups !== undefined && canUseRollups(ctx.filters, ctx.options);

  if (useRollups) {
    // Rollups carry no per-category count, so it is left null rather than invented.
    for (const [categoryId, paise] of Object.entries(sumRollups(rollups).byCategory)) {
      totals.set(categoryId, { paise, count: 0 });
    }
  } else {
    for (const txn of transactions) {
      if (classifyOutflow(txn, ctx.accountType) !== 'expense') continue;
      const key = txn.categoryId ?? UNCATEGORISED;
      const current = totals.get(key) ?? { paise: 0, count: 0 };
      totals.set(key, { paise: current.paise + txn.amountInrPaise, count: current.count + 1 });
    }
  }

  const grand = [...totals.values()].reduce((sum, t) => sum + t.paise, 0);

  const rows = [...totals.entries()]
    .sort((a, b) => b[1].paise - a[1].paise)
    .map(([categoryId, total]) => ({
      cells: {
        category: categoryId === UNCATEGORISED ? 'Uncategorised' : ctx.categoryName(categoryId),
        count: useRollups ? null : total.count,
        amount: total.paise,
        // Basis points, so the exporter can divide by 10,000 for a real Excel percentage
        // rather than writing the string "12.3%".
        share: grand === 0 ? 0 : Math.round((total.paise / grand) * 10_000),
      } satisfies Record<string, Cell>,
    }));

  return {
    id: 'category',
    title: 'Category-wise',
    periodLabel: periodLabel(ctx.filters.period),
    columns: CATEGORY_COLUMNS,
    rows,
    totals: {
      cells: {
        category: 'Total',
        count: useRollups ? null : rows.reduce((s, r) => s + Number(r.cells.count ?? 0), 0),
        amount: grand,
        share: grand === 0 ? 0 : 10_000,
      },
      emphasis: true,
    },
    source: useRollups ? 'rollups' : 'transactions',
    appliedFilters: ctx.appliedFilters,
  };
}

// ---------------------------------------------------------------------------
// FR-RPT-05 — income vs expense
// ---------------------------------------------------------------------------

const INCOME_EXPENSE_COLUMNS: ReportColumn[] = [
  { key: 'month', label: 'Month', type: 'text' },
  { key: 'income', label: 'Income', type: 'money' },
  { key: 'expenses', label: 'Expenses', type: 'money' },
  { key: 'net', label: 'Net', type: 'money' },
];

export function incomeVsExpenseReport(
  transactions: Transaction[],
  ctx: ReportContext,
  rollups?: Rollup[],
): Report {
  const months = monthlyFigures(transactions, ctx, rollups);

  const rows = months.map((m) => ({
    cells: {
      month: formatMonthKey(m.monthKey),
      income: m.incomePaise,
      expenses: m.expensePaise,
      net: m.incomePaise - m.expensePaise,
    } satisfies Record<string, Cell>,
  }));

  const income = months.reduce((s, m) => s + m.incomePaise, 0);
  const expenses = months.reduce((s, m) => s + m.expensePaise, 0);

  return {
    id: 'income-expense',
    title: 'Income vs expense',
    periodLabel: periodLabel(ctx.filters.period),
    columns: INCOME_EXPENSE_COLUMNS,
    rows,
    totals: {
      cells: { month: 'Total', income, expenses, net: income - expenses },
      emphasis: true,
    },
    source: usedRollups(ctx, rollups) ? 'rollups' : 'transactions',
    appliedFilters: ctx.appliedFilters,
  };
}

// ---------------------------------------------------------------------------
// FR-RPT-07 — monthly summary
// ---------------------------------------------------------------------------

const MONTHLY_COLUMNS: ReportColumn[] = [
  { key: 'month', label: 'Month', type: 'text' },
  { key: 'count', label: 'Entries', type: 'number' },
  { key: 'income', label: 'Income', type: 'money' },
  { key: 'expenses', label: 'Expenses', type: 'money' },
  { key: 'savings', label: 'Savings', type: 'money' },
  { key: 'outflow', label: 'Total outflow', type: 'money' },
  { key: 'net', label: 'Net', type: 'money' },
];

export function monthlySummaryReport(
  transactions: Transaction[],
  ctx: ReportContext,
  rollups?: Rollup[],
): Report {
  const months = monthlyFigures(transactions, ctx, rollups);

  const rows = months.map((m) => ({
    cells: {
      month: formatMonthKey(m.monthKey),
      count: m.txnCount,
      income: m.incomePaise,
      expenses: m.expensePaise,
      savings: m.savingsPaise,
      // CLAUDE.md, confirmed 3 Aug 2026: total outflow = expenses + savings. Own-account
      // transfers and money lent out are in none of the three.
      outflow: m.expensePaise + m.savingsPaise,
      net: m.incomePaise - (m.expensePaise + m.savingsPaise),
    } satisfies Record<string, Cell>,
  }));

  const sum = (key: keyof MonthFigures) =>
    months.reduce((s, m) => s + (m[key] as number), 0);
  const expenses = sum('expensePaise');
  const savings = sum('savingsPaise');
  const income = sum('incomePaise');

  return {
    id: 'monthly',
    title: 'Monthly summary',
    periodLabel: periodLabel(ctx.filters.period),
    columns: MONTHLY_COLUMNS,
    rows,
    totals: {
      cells: {
        month: 'Total',
        count: sum('txnCount'),
        income,
        expenses,
        savings,
        outflow: expenses + savings,
        net: income - (expenses + savings),
      },
      emphasis: true,
    },
    source: usedRollups(ctx, rollups) ? 'rollups' : 'transactions',
    appliedFilters: ctx.appliedFilters,
  };
}

// ---------------------------------------------------------------------------
// Shared monthly aggregation
// ---------------------------------------------------------------------------

interface MonthFigures {
  monthKey: MonthKey;
  incomePaise: Paise;
  expensePaise: Paise;
  savingsPaise: Paise;
  txnCount: number;
}

function usedRollups(ctx: ReportContext, rollups?: Rollup[]): boolean {
  return rollups !== undefined && canUseRollups(ctx.filters, ctx.options);
}

/**
 * One month-by-month aggregation, shared by the two monthly reports.
 *
 * Every month in the period appears, including empty ones — a summary with June missing
 * reads as data loss rather than as a quiet month.
 */
function monthlyFigures(
  transactions: Transaction[],
  ctx: ReportContext,
  rollups?: Rollup[],
): MonthFigures[] {
  const months = periodMonths(ctx.filters.period, ctx.options);
  const blank = () => ({ incomePaise: 0, expensePaise: 0, savingsPaise: 0, txnCount: 0 });
  const byMonth = new Map(months.map((m) => [m, blank()]));

  if (usedRollups(ctx, rollups)) {
    for (const rollup of rollups ?? []) {
      const target = byMonth.get(rollup.monthKey);
      if (!target) continue;
      target.incomePaise += rollup.incomePaise;
      target.expensePaise += rollup.expensePaise;
      target.savingsPaise += rollup.savingsPaise;
      target.txnCount += rollup.txnCount;
    }
  } else {
    for (const txn of transactions) {
      const target = byMonth.get(monthKeyOf(txn.dateKey));
      if (!target) continue;
      target.txnCount += 1;
      const kind = classifyOutflow(txn, ctx.accountType);
      if (txn.type === 'income') target.incomePaise += txn.amountInrPaise;
      else if (kind === 'expense') target.expensePaise += txn.amountInrPaise;
      else if (kind === 'savings') target.savingsPaise += txn.amountInrPaise;
    }
  }

  return months.map((monthKey) => ({ monthKey, ...byMonth.get(monthKey)! }));
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

export function buildReport(
  id: ReportId,
  transactions: Transaction[],
  ctx: ReportContext,
  rollups?: Rollup[],
): Report {
  switch (id) {
    case 'detailed':
      return detailedReport(transactions, ctx);
    case 'category':
      return categoryReport(transactions, ctx, rollups);
    case 'income-expense':
      return incomeVsExpenseReport(transactions, ctx, rollups);
    case 'monthly':
      return monthlySummaryReport(transactions, ctx, rollups);
  }
}

/** Columns for a template. The personal PDF drops payee and reference (FR-RPT-13). */
export function columnsFor(report: Report, template: 'personal' | 'business'): ReportColumn[] {
  return template === 'business'
    ? report.columns
    : report.columns.filter((column) => !column.businessOnly);
}

/**
 * Which template a report defaults to (FR-RPT-14).
 *
 * Business only when the report is explicitly filtered to business. "Both" is a personal
 * document: putting a company letterhead on a mixed statement misrepresents it.
 */
export function defaultTemplate(filters: Filters): 'personal' | 'business' {
  return filters.isBusiness === true ? 'business' : 'personal';
}

/** A stable, descriptive filename stem. */
export function reportFilename(report: Report, extension: string): string {
  const slug = report.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const period = report.periodLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${slug}-${period}.${extension}`;
}
